import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { faArrowLeft, faTimes } from '@fortawesome/pro-light-svg-icons';

import { Icon } from '@/components/ui/icon.component';
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.component';
import { cpfValidator } from '@/utils/validators';
import { StepKeys } from '@/app/(main)/_components/sign-in-modal/_types/header-auth-modal-step-keys.enum';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { setLoginDocumentNumber } from '@/store/login.store';
import { checkIfCpfExists } from '@/services/auth/registration.service';
import { useToast } from '@/components/ui/use-toast';
import { useLoading } from '@/hooks/use-loading.hook';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

const forgotPasswordCpfSchema = z.object({
  documentNumber: z.custom<string>(cpfValidator, {
    message: 'Informe um CPF válido.',
  }),
});

type ForgotPasswordCpfForm = z.infer<typeof forgotPasswordCpfSchema>;

export default function ForgotPasswordCpfStep() {
  const { handleJump: jump } = useProgressiveModal<StepKeys>();
  const { toast } = useToast();
  const { isLoading, startLoading, stopLoading } = useLoading();

  const form = useForm<ForgotPasswordCpfForm>({
    resolver: zodResolver(forgotPasswordCpfSchema),
    defaultValues: { documentNumber: '' },
    mode: 'onSubmit',
  });

  const onSubmit = form.handleSubmit(async ({ documentNumber }) => {
    try {
      startLoading();
      const { exists } = await checkIfCpfExists(documentNumber);
      if (!exists) {
        toast.error('CPF inválido ou não cadastrado.');
        return;
      }
      await setLoginDocumentNumber(documentNumber);
      jump({ key: StepKeys.FORGOT_PASSWORD });
    } catch {
      toast.error('CPF inválido ou não cadastrado.');
    } finally {
      stopLoading();
    }
  });

  return (
    <div className="flex flex-col">
      <SheetHeader className="bg-white">
        <div className="flex justify-between gap-3 pb-6 items-center">
          <div className="flex gap-3">
            {jump && (
              <div className="flex justify-center w-[30px] h-[30px]">
                <Icon
                  icon={faArrowLeft}
                  className="cursor-pointer text-gray-500 text-2xl self-center"
                  onClick={() => jump({ key: StepKeys.LOGIN, returning: true })}
                />
              </div>
            )}
            <SheetTitle>
              <div className="flex gap-3">
                <Typography variant={TypographyVariant.MODAL_TITLE}>Esqueceu a senha?</Typography>
              </div>
            </SheetTitle>
          </div>
          <SheetClose asChild className="text-gray-500 cursor-pointer self-center px-2 hover:bg-gray-50">
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </SheetClose>
        </div>
      </SheetHeader>
      <div className="pb-6">
        <p className="text-gray-800 mb-4">Informe seu CPF para continuar.</p>
        <Form {...form}>
          <form onSubmit={onSubmit}>
            <FormField
              control={form.control}
              name="documentNumber"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="000.000.000-00"
                      mask="999.999.999-99"
                      {...field}
                      inputMode="numeric"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              isLoading={isLoading}
              type="submit"
              color="primary"
              variant="default"
              className="mt-6 w-full h-12"
            >
              Continuar
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
